import type { CSSProperties, ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { X } from "lucide-react";
import { Button } from "@hipotecar/ui/button";
import { cn } from "@hipotecar/ui/lib/utils";
import { formatDate } from "@/utils/formatters";
import { track } from "@/utils/analytics";
import { usePlans } from "../hooks/useSubscriptionQuery";
import { usePlanCheckout } from "../hooks/usePlanCheckout";
import { getPlanPriceLabels } from "../utils/pricingHelpers";
import { isProPlan } from "../utils/planIdentity";

export interface UpgradePaywallMeter {
	label: string;
	/** What the user already has; may exceed the limit after a downgrade. */
	used: number;
	limit: number;
}

export interface UpgradePaywallBenefits {
	/** Lead-in above the list, e.g. "Con Pro ves:". */
	intro: string;
	items: string[];
}

export interface UpgradePaywallProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/**
	 * Stable id of this gate (e.g. "property", "avm_sale") for analytics.
	 * Independent of the translated copy, so insights survive copy changes.
	 */
	gateId: string;
	/** `source` of the checkout the upgrade button starts. */
	checkoutSource: string;
	/** Where "Comparar planes" goes; defaults to the pricing page. */
	comparePlansHref?: string;
	title: string;
	/** One line under the title (feature gates). */
	subtitle?: string;
	body?: string;
	meter?: UpgradePaywallMeter;
	/** What Pro unlocks, for gates on a feature rather than a count. */
	benefits?: UpgradePaywallBenefits;
	/** ISO datetime when a renewing quota is back; the line hides without it. */
	renewsAt?: string | null;
}

const RENEWAL_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
	day: "numeric",
	month: "short",
	year: "numeric",
};

// Entrance choreography (ms): the copy rises in top to bottom, the meter
// fills once it is on screen, then the counter pops as the bar lands.
const METER_FILL_DELAY = 300;
const METER_FILL_DURATION = 800;
const METER_LANDED = METER_FILL_DELAY + METER_FILL_DURATION - 50;

const delay = (ms: number): CSSProperties => ({ animationDelay: `${ms}ms` });

const RISE_IN = "motion-safe:animate-rise-in";

/**
 * The redesigned upgrade paywall (Figma "Mensajes Upsell"): "Plan gratis"
 * badge, a title naming what ran out or is locked, then either a usage meter
 * that fills on open (limits) or the benefits Pro unlocks (features), the Pro
 * price, and full-width "Mejorar a Pro" / "Comparar planes".
 *
 * Callers own the copy and when it opens. paywall_viewed is theirs too (some
 * track it where the gate trips); dismiss and CTA clicks are tracked here.
 */
export function UpgradePaywall({
	open,
	onOpenChange,
	gateId,
	checkoutSource,
	comparePlansHref = "/pricing",
	title,
	subtitle,
	body,
	meter,
	benefits,
	renewsAt,
}: UpgradePaywallProps) {
	const { t, i18n } = useTranslation(["pricing"]);
	const navigate = useNavigate();
	const { data: plans } = usePlans();
	const { startCheckout, processingPlanId } = usePlanCheckout();

	const proPlan = plans?.find(isProPlan) ?? null;
	const price = proPlan ? getPlanPriceLabels(proPlan, "mensual", t) : null;
	const proName = t("pricing:plans.pro.name");

	const paywallProps = { gate_id: gateId, path: window.location.pathname };

	const handleOpenChange = (next: boolean) => {
		if (!next) track("paywall_dismissed", paywallProps);
		onOpenChange(next);
	};

	const handleUpgrade = () => {
		track("paywall_cta_clicked", paywallProps);
		if (proPlan) {
			void startCheckout(proPlan, "mensual", checkoutSource);
			return;
		}
		navigate(comparePlansHref);
	};

	const handleComparePlans = () => {
		onOpenChange(false);
		navigate(comparePlansHref);
	};

	return (
		<Dialog.Root open={open} onOpenChange={handleOpenChange}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 z-99999 bg-black/60 backdrop-blur-[2px] data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
				<Dialog.Content
					onOpenAutoFocus={(e) => e.preventDefault()}
					className={cn(
						"fixed top-1/2 left-1/2 z-99999 flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-[409px] -translate-1/2 flex-col gap-[19px] overflow-y-auto rounded-[32px] border px-6 py-[25px] text-left font-inter shadow-2xl sm:px-[37px]",
						"border-slate-200 bg-white dark:border-slate-700 dark:bg-[#151d33]",
						"duration-200 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-bottom-2 data-[state=open]:zoom-in-95",
					)}
				>
					<div className="flex justify-end">
						<Dialog.Close
							aria-label={t("pricing:paywall.close")}
							className="-m-1 rounded-md p-1 text-slate-400 transition-colors hover:text-slate-900 focus-visible:outline-2 focus-visible:outline-brand-500 dark:text-slate-400 dark:hover:text-white"
						>
							<X className="size-[18px]" />
						</Dialog.Close>
					</div>

					<div className="flex flex-col gap-6">
						<div className="flex flex-col gap-2">
							<p
								className={cn(
									RISE_IN,
									"text-[11px] leading-[normal] font-bold tracking-widest text-brand-800 uppercase dark:text-brand-500",
								)}
								style={delay(60)}
							>
								{t("pricing:paywall.freePlan")}
							</p>
							<Dialog.Title
								className={cn(
									RISE_IN,
									"font-jakarta text-[27px] leading-[1.18] font-extrabold tracking-[-0.03em] text-slate-950 dark:text-white",
								)}
								style={delay(100)}
							>
								{title}
							</Dialog.Title>
							{subtitle && (
								<Description
									asDescription={!body}
									className={cn(RISE_IN, "text-[14.5px]/[1.4] text-slate-700 dark:text-slate-300")}
									style={delay(120)}
								>
									{subtitle}
								</Description>
							)}
						</div>

						{body && (
							<Description
								asDescription
								className={cn(RISE_IN, "text-[13.5px]/[1.6] text-slate-600 dark:text-slate-400")}
								style={delay(140)}
							>
								{body}
							</Description>
						)}

						{meter && <UsageMeter {...meter} />}
						{benefits && <BenefitList {...benefits} />}
					</div>

					{renewsAt && (
						<p
							className={cn(RISE_IN, "text-[12.5px] leading-[normal] text-slate-500")}
							style={delay(220)}
						>
							{t("pricing:paywall.renews", {
								date: formatDate(renewsAt, i18n.language, RENEWAL_DATE_OPTIONS),
							})}
						</p>
					)}

					<div className="h-px shrink-0 bg-slate-200 dark:bg-slate-800" />

					{price && (
						<p
							className={cn(RISE_IN, "flex items-baseline gap-2 leading-[normal] whitespace-nowrap")}
							style={delay(260)}
						>
							<span className="font-jakarta text-[30px] font-extrabold tracking-[-0.03em] text-slate-950 dark:text-white">
								{price.currencySymbol} {price.amount}
							</span>
							<span className="text-[13px] text-slate-500 dark:text-slate-400">
								{t("pricing:paywall.pricePeriod", {
									period: price.period,
									plan: proName,
									// The period carries a "/" that i18next would escape;
									// React already escapes what we render.
									interpolation: { escapeValue: false },
								})}
							</span>
						</p>
					)}

					<div className={cn(RISE_IN, "flex flex-col gap-[19px]")} style={delay(300)}>
						<Button
							data-attr={`paywall-${gateId}-upgrade`}
							onClick={handleUpgrade}
							isLoading={proPlan !== null && processingPlanId === proPlan.id}
							className="h-12 w-full rounded-lg text-[14.5px] font-bold shadow-none dark:bg-slate-100 dark:text-[#0b0f1a] dark:hover:bg-white"
						>
							{t("pricing:paywall.upgrade", { plan: proName })}
						</Button>
						<button
							type="button"
							data-attr={`paywall-${gateId}-compare-plans`}
							onClick={handleComparePlans}
							className="h-11 w-full rounded-lg border border-slate-200 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-brand-500 dark:border-slate-800 dark:text-slate-300 dark:hover:bg-white/5 dark:hover:text-white"
						>
							{t("pricing:paywall.comparePlans")}
						</button>
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}

interface DescriptionProps {
	/** Whether this paragraph is the dialog's accessible description. */
	asDescription: boolean;
	className: string;
	style: CSSProperties;
	children: ReactNode;
}

/** A paragraph that may also be the dialog's accessible description. */
function Description({ asDescription, className, style, children }: DescriptionProps) {
	return asDescription ? (
		<Dialog.Description className={className} style={style}>
			{children}
		</Dialog.Description>
	) : (
		<p className={className} style={style}>
			{children}
		</p>
	);
}

/**
 * What Pro unlocks. The items rise in one after another and their yellow
 * dots pop, echoing the meter's landing on the limit variants.
 */
function BenefitList({ intro, items }: UpgradePaywallBenefits) {
	return (
		<div className="flex flex-col gap-2.5">
			<p className={cn(RISE_IN, "text-[13.5px]/[1.6] text-slate-600 dark:text-slate-400")} style={delay(160)}>
				{intro}
			</p>
			<ul className="flex flex-col gap-2.5">
				{items.map((item, index) => (
					<li
						key={item}
						className={cn(
							RISE_IN,
							"flex items-center gap-2.5 text-[13.5px] leading-[normal] font-medium text-slate-800 dark:text-[#d9e0eb]",
						)}
						style={delay(200 + index * 70)}
					>
						<span
							aria-hidden
							className="size-1.5 shrink-0 rounded-full bg-brand-500 motion-safe:animate-dot-pop"
							style={delay(320 + index * 70)}
						/>
						{item}
					</li>
				))}
			</ul>
		</div>
	);
}

/**
 * The used-up quota as a full bar. On open the bar fills from empty, a light
 * band sweeps across it as it lands and the counter pops, so the limit reads
 * as something that just happened. Reduced motion shows it full right away.
 */
function UsageMeter({ label, used, limit }: UpgradePaywallMeter) {
	return (
		<div className={cn(RISE_IN, "flex flex-col gap-2")} style={delay(180)}>
			<div className="flex items-start justify-between text-xs/normal font-semibold whitespace-nowrap">
				<span className="text-slate-500 dark:text-slate-400">{label}</span>
				<span
					className="inline-block text-slate-950 tabular-nums motion-safe:animate-count-pop dark:text-white"
					style={delay(METER_LANDED)}
				>
					{used} / {limit}
				</span>
			</div>
			<div
				role="progressbar"
				aria-label={label}
				aria-valuemin={0}
				aria-valuemax={limit}
				aria-valuenow={Math.min(used, limit)}
				className="relative h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800"
			>
				<div
					className="h-full origin-left rounded-full bg-brand-500 motion-safe:animate-meter-fill"
					style={{ animationDelay: `${METER_FILL_DELAY}ms`, animationDuration: `${METER_FILL_DURATION}ms` }}
				/>
				<div
					aria-hidden
					className="absolute inset-y-0 left-0 w-1/4 bg-linear-to-r from-transparent via-white/70 to-transparent opacity-0 motion-safe:animate-meter-sheen"
					style={delay(METER_LANDED)}
				/>
			</div>
		</div>
	);
}
